'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { track } from '@/lib/analytics/track'
import { renderMarkdownContent } from '@/lib/marketing/markdown'

interface SamplePreviewProps {
  sampleInput: string
  sampleOutput: string
  skillTitle: string
  skillSlug: string
  iconEmoji?: string
}

const INPUT_CHAR_MS = 3 // typing speed for input
const OUTPUT_TOKEN_MS = 18 // streaming speed for output tokens

export function SamplePreview({
  sampleInput,
  sampleOutput,
  skillTitle,
  skillSlug,
  iconEmoji,
}: SamplePreviewProps) {
  const [inputText, setInputText] = useState('')
  const [outputText, setOutputText] = useState('')
  const [phase, setPhase] = useState<'idle' | 'input' | 'output' | 'done'>(
    'idle',
  )
  const hasStarted = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Start animation when component scrolls into view
  useEffect(() => {
    if (hasStarted.current) return
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted.current) {
          hasStarted.current = true
          setPhase('input')
          observer.disconnect()
        }
      },
      { threshold: 0.3 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Phase: input — type out "/skill-slug " prefix + sampleInput
  const prefix = `@${skillSlug} `
  const fullInput = prefix + sampleInput
  useEffect(() => {
    if (phase !== 'input') return
    let i = 0
    const id = setInterval(() => {
      i++
      setInputText(fullInput.slice(0, i))
      if (i >= fullInput.length) {
        clearInterval(id)
        // Small pause before output starts
        setTimeout(() => setPhase('output'), 400)
      }
    }, INPUT_CHAR_MS)
    return () => clearInterval(id)
  }, [phase, fullInput])

  // Phase: output — stream tokens (split by whitespace, keep markdown intact)
  useEffect(() => {
    if (phase !== 'output') return
    const tokens = sampleOutput.split(/(\s+)/)
    let i = 0
    let accumulated = ''
    const id = setInterval(() => {
      if (i >= tokens.length) {
        clearInterval(id)
        setPhase('done')
        return
      }
      accumulated += tokens[i]
      i++
      setOutputText(accumulated)
    }, OUTPUT_TOKEN_MS)
    return () => clearInterval(id)
  }, [phase, sampleOutput])

  const isStreaming = phase === 'input' || phase === 'output'
  const showCursor = phase === 'output'

  return (
    <div ref={containerRef} className="flex h-[640px] flex-col gap-4">
      {/* Input card */}
      <div className="rounded-2xl border border-white/40 bg-white/50 p-5 shadow-sm backdrop-blur-md">
        <p className="mb-2 text-xs font-medium tracking-wider text-neutral-500 uppercase">
          Sample input of the skill
        </p>
        <div className="space-y-3 rounded-xl border border-neutral-200 bg-white px-5 py-4">
          <p className="text-base text-neutral-900">
            {phase === 'idle' ? (
              <span className="text-neutral-300">|</span>
            ) : (
              <>
                &ldquo;
                {(() => {
                  const visible = inputText
                  const prefixVisible = visible.slice(0, prefix.length)
                  const restVisible = visible.slice(prefix.length)
                  return (
                    <>
                      {prefixVisible && (
                        <mark className="rounded-sm bg-amber-200/70 px-0.5 font-medium text-neutral-900">
                          {prefixVisible}
                        </mark>
                      )}
                      {restVisible}
                    </>
                  )
                })()}
                {phase === 'input' && <span className="animate-pulse">|</span>}
                {phase !== 'input' && inputText && <>&rdquo;</>}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Output card — flex-1 fills remaining height */}
      <div className="flex min-h-0 flex-1 flex-col rounded-2xl border border-white/40 bg-white/50 p-5 shadow-sm backdrop-blur-md">
        <p className="mb-3 text-xs font-medium tracking-wider text-neutral-500 uppercase">
          Sample Output in SOKU
        </p>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-neutral-100 bg-white/60 p-4">
          {outputText ? (
            <div className="prose prose-sm prose-neutral prose-headings:text-neutral-900 prose-headings:font-semibold max-w-none text-neutral-800">
              {renderMarkdownContent(outputText)}
              {showCursor && (
                <span className="inline-block h-4 w-0.5 animate-pulse bg-neutral-400 align-text-bottom" />
              )}
            </div>
          ) : (
            <div className="flex min-h-20 items-center justify-center">
              {isStreaming ? (
                <span className="animate-pulse text-sm text-neutral-400">
                  Generating...
                </span>
              ) : (
                <span className="text-sm text-neutral-300">
                  Output will appear here
                </span>
              )}
            </div>
          )}
        </div>

        {/* CTA — visible from output phase onward */}
        {(phase === 'output' || phase === 'done') && (
          <Link
            href="/sign-up"
            onClick={() =>
              track('skill_detail_cta_click', {
                slug: skillSlug,
                cta_location: 'hero',
              })
            }
            className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-neutral-900 p-3.5 text-center text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Try your own scenario in Soku AI
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>
    </div>
  )
}
